/**
 * Support Chat (user side) — /support
 *
 * A self-contained 1:1 chat between the signed-in user and the Arab Sanad
 * support team. Visually consistent with the dark Royal-Blue theme used by
 * the existing inbox, but isolated from the user-to-user and AI chats:
 *   - Loads/auto-creates the user's open conversation from /api/support/conversation.
 *   - Sends messages via POST /api/support/messages.
 *   - Subscribes to /api/ws/chat for { type: "support.new" } pushes — no polling.
 *   - Keeps a slow 30s REST refetch as a safety net if the socket disconnects.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, useLanguage, API } from "@/App";
import { DashboardLayout } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Headphones, Send, Paperclip, Loader2, ShieldCheck } from "lucide-react";

export default function SupportChat() {
  const { token, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState("");
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const isAr = language === "ar";

  useEffect(() => { if (!token) navigate("/login"); }, [token, navigate]);

  // Load conversation + history. Marks user-side as read on the backend.
  const load = async () => {
    try {
      const r = await axios.get(`${API}/support/conversation`, { headers: { Authorization: `Bearer ${token}` } });
      setConv(r.data.conversation);
      setMessages(r.data.messages || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ── Realtime WebSocket + 30s polling fallback ─────────────────────────
  useEffect(() => {
    if (!token) return;
    load();
    let ws = null;
    let reconnectTimer = null;
    const cleanup = { cancelled: false };
    const apiBase = API.replace(/\/api$/, "");
    const wsBase = apiBase.replace(/^http/, "ws");
    const connect = () => {
      if (cleanup.cancelled) return;
      try { ws = new WebSocket(`${wsBase}/api/ws/chat?token=${encodeURIComponent(token)}`); }
      catch { return; }
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data?.type === "support.new") {
            // Only ingest messages for THIS user's conversation
            setMessages((prev) => prev.some((m) => m.message_id === data.message.message_id) ? prev : [...prev, data.message]);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { if (!cleanup.cancelled) reconnectTimer = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws && ws.close(); } catch { /* noop */ } };
    };
    connect();
    const iv = setInterval(load, 30000); // slow safety net
    return () => {
      cleanup.cancelled = true;
      clearInterval(iv);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) { try { ws.close(); } catch { /* noop */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Scroll to bottom on new messages.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    // Optimistic UI — drop in a temp message so the bubble appears instantly.
    const optimistic = {
      message_id:  `tmp_${Date.now()}`,
      conv_id:     conv?.conv_id,
      sender_id:   user?.user_id,
      sender_role: "user",
      sender_name: user?.name || "",
      content:     text,
      created_at:  new Date().toISOString(),
      _pending:    true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    try {
      const r = await axios.post(
        `${API}/support/messages`,
        { content: text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Swap optimistic with the real message_id from server.
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.message_id !== optimistic.message_id);
        return filtered.some((m) => m.message_id === r.data.message.message_id)
          ? filtered
          : [...filtered, r.data.message];
      });
    } catch (e) {
      toast.error(t("تعذّر إرسال الرسالة. حاول مرة أخرى.", "Could not send the message. Try again."));
      // Remove optimistic on failure
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  // Attachment upload — small images/files routed through the existing
  // /api/uploads endpoint, then we send a message with attachment_url.
  const onAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const up = await axios.post(`${API}/uploads`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = up.data?.url || up.data?.file_url || up.data?.location;
      const type = file.type.startsWith("image/") ? "image"
                  : file.type.startsWith("video/") ? "video"
                  : file.type.startsWith("audio/") ? "audio" : "file";
      const r = await axios.post(
        `${API}/support/messages`,
        { content: "", attachment_url: url, attachment_type: type },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessages((prev) => [...prev, r.data.message]);
    } catch (err) {
      toast.error(t("تعذّر رفع الملف.", "File upload failed."));
    } finally {
      setSending(false);
      e.target.value = "";
    }
  };

  const closedBanner = useMemo(
    () => conv?.status === "closed" ? (isAr ? "تم إغلاق هذه المحادثة من قبل الدعم. أي رسالة جديدة ستفتح محادثة جديدة." : "This conversation was closed by support. A new message will open a fresh thread.") : null,
    [conv?.status, isAr]
  );

  return (
    <DashboardLayout title={t("الدعم الفني", "Support Chat")}>
      <div className="max-w-3xl mx-auto" data-testid="support-chat-page">
        {/* Header */}
        <div className="glass-card rounded-2xl px-4 py-3 mb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-500/15 border border-yellow-500/30 flex items-center justify-center">
              <Headphones className="text-yellow-400" size={20} />
            </div>
            <div>
              <div className="text-white font-bold text-base">{t("دعم Arab Sanad", "Arab Sanad Support")}</div>
              <div className="text-[11px] text-slate-400 flex items-center gap-1">
                <ShieldCheck size={12} className="text-emerald-400" />
                {t("فريقنا يرد عادةً خلال ساعات قليلة", "Our team usually replies within hours")}
              </div>
            </div>
          </div>
          {conv?.status && (
            <Badge className={conv.status === "open" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-slate-600/30 text-slate-300 border-slate-500/40"} data-testid="support-status-badge">
              {conv.status === "open" ? t("مفتوحة", "Open") : t("مغلقة", "Closed")}
            </Badge>
          )}
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="glass-card rounded-2xl p-3 sm:p-4 h-[60vh] sm:h-[62vh] overflow-y-auto space-y-2"
          data-testid="support-messages"
        >
          {loading ? (
            <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin text-yellow-500" /></div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4" data-testid="support-empty-state">
              <Headphones className="text-slate-600 mb-3" size={42} />
              <p className="text-slate-400 text-sm">{t("لا توجد رسائل هنا بعد", "No messages here yet")}</p>
              <p className="text-slate-500 text-xs mt-1">{t("ابدأ المحادثة بإرسال أول رسالة لفريق الدعم.", "Start the conversation by sending your first message to support.")}</p>
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_role === "user";
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.message_id}`}>
                  <div className={`max-w-[80%] sm:max-w-[70%] rounded-2xl px-3 py-2 ${
                    mine
                      ? "bg-blue-600/90 text-white rounded-br-sm"
                      : "bg-slate-800/80 text-slate-100 rounded-bl-sm border border-slate-700"
                  } ${m._pending ? "opacity-70" : ""}`}>
                    {!mine && (
                      <div className="text-[10px] text-yellow-300 font-semibold mb-0.5 inline-flex items-center gap-1">
                        <ShieldCheck size={10} />{m.sender_name || t("الدعم", "Support")}
                      </div>
                    )}
                    {m.attachment_url && m.attachment_type === "image" && (
                      <a href={m.attachment_url} target="_blank" rel="noopener noreferrer">
                        <img src={m.attachment_url} alt="" className="rounded-lg max-h-64 mb-1" />
                      </a>
                    )}
                    {m.attachment_url && m.attachment_type !== "image" && (
                      <a href={m.attachment_url} target="_blank" rel="noopener noreferrer" className="block text-xs underline text-yellow-200 mb-1">
                        {t("ملف مرفق", "Attached file")}
                      </a>
                    )}
                    {m.content && <div className="text-sm whitespace-pre-wrap break-words">{m.content}</div>}
                    <div className="text-[10px] opacity-60 mt-0.5 text-end">
                      {new Date(m.created_at).toLocaleString(isAr ? "ar-EG" : undefined, { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {closedBanner && (
          <div className="mt-2 text-[11px] text-slate-400 bg-slate-900/60 border border-slate-800 rounded-xl px-3 py-2">{closedBanner}</div>
        )}

        {/* Input bar */}
        <div className="mt-3 flex items-end gap-2">
          <input ref={fileInputRef} type="file" accept="image/*,video/*,audio/*,application/pdf" onChange={onAttach} className="hidden" data-testid="support-attach-input" />
          <Button
            variant="outline"
            className="border-slate-700 text-slate-200 hover:bg-slate-800 h-11 w-11 p-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            data-testid="support-attach-btn"
            aria-label={t("إرفاق", "Attach")}
          >
            <Paperclip size={18} />
          </Button>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t("اكتب رسالتك للدعم…", "Type your message to support…")}
            className="flex-1 h-11 bg-slate-900/70 border-slate-700 text-white placeholder:text-slate-500"
            disabled={sending}
            data-testid="support-input"
          />
          <Button
            onClick={send}
            disabled={sending || !draft.trim()}
            className="btn-primary h-11 w-11 p-0"
            data-testid="support-send-btn"
            aria-label={t("إرسال", "Send")}
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
